import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import reklamacjeService from '../services/reklamacjeService';
import {
  FaSearch,
  FaTimes,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaFilePdf,
  FaExclamationTriangle,
  FaChevronLeft,
  FaChevronRight,
  FaBoxOpen,
  FaSyncAlt
} from 'react-icons/fa';

/**
 * Lista reklamacji (do dostawcy) zalogowanego uzytkownika.
 * - nie-admin: reklamacje jego lokalizacji (backend location_sql_filter)
 * - admin: wszystkie (globalny widok admina jest osobnym komponentem)
 *
 * Kolumny: numer, data, kontrahent, towar(y) (liczba pozycji), status (kolor),
 * rozliczona, nr korekty. Filtry: grupa (biezace/zakonczone), status, daty,
 * rozliczona, szukaj. Przycisk "Nowa reklamacja" -> /reklamacje/nowa.
 *
 * UI standard V3SYS: font 12px/11px, padding 6px 12px / 4px 10px, tylko tokeny
 * kolorow (var(--...)), toggle aktywny = pelne tlo + #fff.
 */

// Konfiguracja statusow -> kolory (tokeny + light warianty) i etykiety PL.
const STATUS_CONFIG = {
  nowa:          { bg: 'var(--info-light)',    color: 'var(--info)',    text: 'Nowa' },
  w_trakcie:     { bg: 'var(--warning-light)', color: 'var(--warning)', text: 'W trakcie' },
  uwzgledniona:  { bg: 'var(--success-light)', color: 'var(--success)', text: 'Uwzgledniona' },
  odrzucona:     { bg: 'var(--danger-light)',  color: 'var(--danger)',  text: 'Odrzucona' }
};

const inputStyle = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '12px',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
  outline: 'none'
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: '11px',
  fontWeight: '500',
  color: 'var(--text-muted)'
};

const EMPTY_FILTERS = {
  grupa: '',
  status: '',
  rozliczona: '',
  data_od: '',
  data_do: '',
  search: ''
};

const formatDate = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleDateString('pl-PL');
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { bg: 'var(--border)', color: 'var(--text)', text: status || 'Brak' };
  return (
    <span style={{
      padding: '0.15rem 0.45rem',
      backgroundColor: cfg.bg,
      color: cfg.color,
      borderRadius: '3px',
      fontSize: '10px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    }}>
      {cfg.text}
    </span>
  );
};

const ReklamacjePage = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { hasPermission } = usePermissions();

  const canCreate = hasPermission('reklamacje', 'tworzenie');
  const canEdit = hasPermission('reklamacje', 'edycja');
  const canDelete = hasPermission('reklamacje', 'usuwanie');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });

  // Guard sekwencji odpowiedzi (wyscig przy szybkiej zmianie filtrow)
  const loadSeqRef = useRef(0);

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pagination.per_page));

  const loadReklamacje = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      setError(null);

      // Wysylamy tylko niepuste filtry, by nie zasmiecac query-stringa
      const params = { page: pagination.page, per_page: pagination.per_page };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });

      const resp = await reklamacjeService.getAll(params);
      if (seq !== loadSeqRef.current) return; // przestarzala odpowiedz

      const data = resp?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setPagination(prev => ({
        ...prev,
        total: data.total ?? 0,
        page: data.page ?? prev.page,
        per_page: data.per_page ?? prev.per_page
      }));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(err?.message || 'Blad pobierania reklamacji');
      setItems([]);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [filters, pagination.page, pagination.per_page]);

  // Debounce: nie strzelaj zapytaniem przy kazdym znaku w wyszukiwarce.
  useEffect(() => {
    const timer = setTimeout(() => { loadReklamacje(); }, 300);
    return () => clearTimeout(timer);
  }, [loadReklamacje]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const openNew = () => navigate('/reklamacje/nowa');
  const openView = (rid) => navigate(`/reklamacje/${rid}`);
  const openEdit = (rid) => navigate(`/reklamacje/${rid}/edytuj`);
  const openPdf = (rid) => navigate(`/reklamacje/${rid}/pdf-podglad`);

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const rid = deleteConfirm.id;
    try {
      await reklamacjeService.remove(rid, false);
      toast.success(`Usunieto reklamacje ${deleteConfirm.numer_reklamacji || `#${rid}`}`);
      setDeleteConfirm(null);
      // Optymistycznie usun z listy, potem odswiez licznik/paginacje
      setItems(prev => prev.filter(x => x.id !== rid));
      loadReklamacje();
    } catch (err) {
      toast.error(err?.message || 'Blad usuwania reklamacji');
      setDeleteConfirm(null);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '' && v !== null && v !== undefined);

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaExclamationTriangle style={{ color: 'var(--warning)', fontSize: '14px' }} />
              Reklamacje
              {selectedLocation && (
                <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-muted)' }}>
                  {selectedLocation.nazwa || selectedLocation.name}
                </span>
              )}
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Reklamacje do dostawcy | Razem: {pagination.total}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => loadReklamacje()}
              disabled={loading}
            >
              <FaSyncAlt style={{ fontSize: '10px', marginRight: '4px' }} />
              {loading ? 'Laduje...' : 'Odswiez'}
            </button>
            {canCreate && (
              <button className="btn btn-primary" onClick={openNew}>
                <FaPlus style={{ fontSize: '10px', marginRight: '4px' }} />
                Nowa reklamacja
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Alert bledow */}
      {error && (
        <div style={{
          padding: '0.5rem 0.75rem',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--danger)' }}
          >×</button>
        </div>
      )}

      {/* Filtry */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        marginBottom: '0.5rem',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '0.5rem 0.75rem',
          borderBottom: '1px solid var(--border)',
          fontWeight: '600',
          fontSize: '12px',
          backgroundColor: 'var(--bg)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Wyszukiwanie i filtry</span>
          {hasActiveFilters && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              Wynikow: <strong>{items.length}</strong>
            </span>
          )}
        </div>
        <div style={{ padding: '0.5rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '0.5rem',
            marginBottom: '0.5rem'
          }}>
            <div>
              <label style={labelStyle}>Szukaj</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Numer / kontrahent / towar / osoba..."
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Grupa</label>
              <select
                value={filters.grupa}
                onChange={(e) => handleFilterChange('grupa', e.target.value)}
                style={inputStyle}
              >
                <option value="">Wszystkie</option>
                <option value="biezace">Biezace (nowa / w trakcie)</option>
                <option value="zakonczone">Zakonczone (uwzgl. / odrzuc.)</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                style={inputStyle}
              >
                <option value="">Wszystkie</option>
                <option value="nowa">Nowa</option>
                <option value="w_trakcie">W trakcie</option>
                <option value="uwzgledniona">Uwzgledniona</option>
                <option value="odrzucona">Odrzucona</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Rozliczenie</label>
              <select
                value={filters.rozliczona}
                onChange={(e) => handleFilterChange('rozliczona', e.target.value)}
                style={inputStyle}
              >
                <option value="">Wszystkie</option>
                <option value="1">Rozliczone</option>
                <option value="0">Nierozliczone</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Data od</label>
              <input
                type="date"
                value={filters.data_od}
                onChange={(e) => handleFilterChange('data_od', e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Data do</label>
              <input
                type="date"
                value={filters.data_do}
                onChange={(e) => handleFilterChange('data_do', e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => loadReklamacje()}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--primary)',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--primary)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FaSearch style={{ fontSize: '9px' }} />
              Szukaj
            </button>
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--text-muted)',
                backgroundColor: 'var(--bg)',
                color: 'var(--text-secondary)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FaTimes style={{ fontSize: '9px' }} />
              Wyczysc
            </button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '0.5rem 0.75rem',
          borderBottom: '1px solid var(--border)',
          fontWeight: '600',
          fontSize: '12px',
          backgroundColor: 'var(--bg)'
        }}>
          Reklamacje ({pagination.total})
        </div>

        {loading && items.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2.5rem 1rem' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
            <p style={{ margin: 0, fontSize: '12px' }}>Ladowanie reklamacji...</p>
          </div>
        ) : items.length === 0 ? (
          // Pusty stan — zachecajacy do zalozenia pierwszej reklamacji
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '3rem 1rem' }}>
            <FaBoxOpen style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.35 }} />
            <p style={{ margin: '0 0 0.35rem', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {hasActiveFilters ? 'Brak reklamacji dla wybranych filtrow' : 'Nie masz jeszcze zadnych reklamacji'}
            </p>
            <p style={{ margin: '0 0 1rem', fontSize: '11px' }}>
              {hasActiveFilters
                ? 'Zmien lub wyczysc filtry, aby zobaczyc pozostale reklamacje.'
                : 'Zloz reklamacje do dostawcy za wadliwy lub uszkodzony towar — dodaj pozycje, zdjecia i wyslij protokol.'}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={clearFilters}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--text-muted)',
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-secondary)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <FaTimes style={{ fontSize: '10px', marginRight: '4px' }} />
                Wyczysc filtry
              </button>
            ) : canCreate && (
              <button
                onClick={openNew}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--primary)',
                  backgroundColor: 'var(--primary)',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                <FaPlus style={{ fontSize: '10px', marginRight: '4px' }} />
                Nowa reklamacja
              </button>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '110px' }}>Numer</th>
                  <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '85px', whiteSpace: 'nowrap' }}>Data</th>
                  <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '150px' }}>Kontrahent</th>
                  <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', width: '70px' }} title="Liczba pozycji (towarow)">Towar(y)</th>
                  <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)' }}>Status</th>
                  <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)' }}>Rozliczona</th>
                  <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '110px' }}>Nr korekty</th>
                  <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '120px' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, index) => (
                  <tr
                    key={r.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer'
                    }}
                    onClick={() => openView(r.id)}
                  >
                    <td style={{ padding: '0.4rem', fontWeight: '600' }}>
                      {r.numer_reklamacji || `#${r.id}`}
                    </td>
                    <td style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>
                      {formatDate(r.created_at)}
                    </td>
                    <td style={{ padding: '0.4rem', maxWidth: '220px' }}>
                      <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.kontrahent_nazwa || 'Nieznany'}
                      </div>
                      {r.osoba_imie_nazwisko && (
                        <div style={{ fontSize: '9px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.osoba_imie_nazwisko}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      <span style={{
                        backgroundColor: 'var(--info-light)',
                        color: 'var(--info)',
                        padding: '0.15rem 0.45rem',
                        borderRadius: '3px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {r.liczba_pozycji ?? 0}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      <StatusBadge status={r.status} />
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                      {Number(r.rozliczona) === 1 ? (
                        <span style={{
                          backgroundColor: 'var(--success-light)',
                          color: 'var(--success)',
                          padding: '0.15rem 0.45rem',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          Tak
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>Nie</span>
                      )}
                    </td>
                    <td style={{ padding: '0.4rem' }}>
                      {r.numer_korekty_zewn
                        ? <span style={{ fontWeight: '500' }}>{r.numer_korekty_zewn}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: '4px' }}>
                        <button
                          onClick={() => openView(r.id)}
                          title="Podglad"
                          style={{
                            padding: '4px 8px',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--card-bg)',
                            color: 'var(--primary)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => openPdf(r.id)}
                          title="Protokol PDF"
                          style={{
                            padding: '4px 8px',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--card-bg)',
                            color: 'var(--danger)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          <FaFilePdf />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => openEdit(r.id)}
                            title="Edytuj"
                            style={{
                              padding: '4px 8px',
                              border: '1px solid var(--border)',
                              backgroundColor: 'var(--card-bg)',
                              color: 'var(--warning)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            <FaEdit />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => setDeleteConfirm(r)}
                            title="Usun"
                            style={{
                              padding: '4px 8px',
                              border: '1px solid var(--border)',
                              backgroundColor: 'var(--card-bg)',
                              color: 'var(--danger)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginacja */}
        {items.length > 0 && (
          <div style={{
            padding: '0.75rem',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '1rem' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pokaz:</span>
                <select
                  value={pagination.per_page}
                  onChange={(e) => setPagination(prev => ({ ...prev, per_page: parseInt(e.target.value, 10), page: 1 }))}
                  style={{ ...inputStyle, width: 'auto', padding: '6px 10px' }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  backgroundColor: pagination.page <= 1 ? 'var(--bg)' : 'var(--card-bg)',
                  color: pagination.page <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FaChevronLeft style={{ fontSize: '9px' }} />
                Poprzednia
              </button>

              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', minWidth: '80px', textAlign: 'center' }}>
                Strona {pagination.page} / {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= totalPages}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  backgroundColor: pagination.page >= totalPages ? 'var(--bg)' : 'var(--card-bg)',
                  color: pagination.page >= totalPages ? 'var(--text-muted)' : 'var(--text-secondary)',
                  cursor: pagination.page >= totalPages ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                Nastepna
                <FaChevronRight style={{ fontSize: '9px' }} />
              </button>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              Pokazuje {Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)} - {Math.min(pagination.page * pagination.per_page, pagination.total)} z {pagination.total} reklamacji
            </div>
          </div>
        )}
      </div>

      {/* Modal potwierdzenia usuniecia */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            padding: '1.5rem',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h5 style={{ marginBottom: '1rem', color: 'var(--danger)', fontSize: '14px' }}>
              Potwierdz usuniecie
            </h5>
            <p style={{ marginBottom: '1rem', fontSize: '12px', color: 'var(--text)' }}>
              Czy na pewno usunac reklamacje <strong>{deleteConfirm.numer_reklamacji || `#${deleteConfirm.id}`}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--text-muted)',
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-secondary)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--danger)',
                  backgroundColor: 'var(--danger)',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                Usun
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReklamacjePage;
